import type { PoolMembershipStats } from '@/types/pool/poolTypes'
import { deriveSponsorDeposits, sumExpectedMemberDepositsWei } from '@/composables/pool/core/assetCalculations'
import { buildMetricDisplayItems } from '../core/metricsFormatting'
import { calculateGridClasses } from '../core/metricsGridLayout'
import { useMemberStats } from './useMemberStats'
import { useVaultMetrics } from './useVaultMetrics'

const BLOCK_POLLING_INTERVAL = 10_000

export interface ReadOnlyMetricsOptions {
  lazy?: boolean
}

export function useReadOnlyMetrics(options: ReadOnlyMetricsOptions = {}) {
  const { lazy = false } = options
  const vaultMetrics = useVaultMetrics({ pollingInterval: BLOCK_POLLING_INTERVAL, lazy })
  const membershipStatsFetch = useMemberStats({ lazy })

  const totalAssets = computed((): bigint => {
    const raw = vaultMetrics.data.value?.totalAssetsRaw
    return (raw != null && raw !== '') ? BigInt(raw) : 0n
  })

  const accruedRewards = computed((): bigint => {
    const raw = vaultMetrics.data.value?.claimableFeesRaw
    return (raw != null && raw !== '') ? BigInt(raw) : 0n
  })

  const baseApy = computed((): number => vaultMetrics.data.value?.aaveApy ?? 0)

  const blockNumber = computed((): string | null => vaultMetrics.data.value?.blockNumber ?? null)

  const stats = computed<PoolMembershipStats | null>(() => {
    const data = membershipStatsFetch.data.value
    if (!data)
      return null

    return {
      totalMembers: data.totalMembers,
      membersByTier: {
        trial: data.membersByTier?.trial ?? 0,
        bronze: data.membersByTier?.bronze ?? 0,
        silver: data.membersByTier?.silver ?? 0,
        gold: data.membersByTier?.gold ?? 0,
      },
    }
  })

  const memberDeposits = computed((): bigint => {
    const statsValue = stats.value
    if (!statsValue?.membersByTier)
      return 0n
    return sumExpectedMemberDepositsWei(statsValue.membersByTier)
  })

  const sponsorDeposits = computed((): bigint => {
    return deriveSponsorDeposits(totalAssets.value, memberDeposits.value)
  })

  const overview = computed(() => {
    return buildMetricDisplayItems({
      baseApy: baseApy.value,
      assets: totalAssets.value,
      memberDeposits: memberDeposits.value,
      sponsorDeposits: sponsorDeposits.value,
      accruedRewards: accruedRewards.value,
      stats: stats.value,
    })
  })

  const gridClasses = computed(() => calculateGridClasses(overview.value.length))

  const isPending = computed(() =>
    vaultMetrics.status.value === 'pending'
    || membershipStatsFetch.status.value === 'pending',
  )

  /**
   * Fetch both sources now. Unlike `refreshNuxtData`, which waits for hydration to finish
   * and the browser to go idle, this starts the requests the moment it is called.
   */
  async function refresh() {
    return Promise.all([vaultMetrics.refresh(), membershipStatsFetch.refresh()])
  }

  return {
    overview,
    ui: {
      gridClasses,
    },
    isPending,
    refresh,
    assets: totalAssets,
    accruedRewards,
    baseApy,
    blockNumber,
    stats,
    memberDeposits,
    sponsorDeposits,
  }
}
